import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { SafeText } from "@/shared/components/SafeText";
import type { AssignableRole } from "./access-client";
import { AccessErrorAlert } from "./AccessErrorAlert";
import { useReactivateMember } from "./use-access-mutations";
import { useAccessSubmit } from "./use-access-submit";

export interface ReactivationOutcome {
  /** `false`: the member was already active, nothing was written. */
  readonly changed: boolean;
  readonly role: AssignableRole;
}

interface ReactivateMemberDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly organizationId: string;
  readonly membershipId: string;
  readonly memberName: string;
  /** The role the row shows (the confirmation text); the OWNER membership is never reactivated here. */
  readonly role: AssignableRole;
  /** Fired after the API accepted the call (including "already active"), right before closing. */
  readonly onDone: (outcome: ReactivationOutcome) => void;
  /**
   * The membership was not in the state this dialog assumed (changed meanwhile,
   * gone...). The reads were refreshed; the dialog closes and hands the reason
   * over so the host shows it next to fresh data.
   */
  readonly onStale: (message: string) => void;
}

/**
 * Confirm-and-reactivate for an INACTIVE ADMIN or STAFF member. A STAFF comes
 * back without locations (the API does not restore them), which the dialog
 * says before confirming. Reactivating sends no notice to the person.
 */
export function ReactivateMemberDialog(props: ReactivateMemberDialogProps) {
  // Mounted only while open (and per membership): the last error starts clean.
  if (!props.open) return null;
  return <ReactivateMemberDialogBody key={props.membershipId} {...props} />;
}

function ReactivateMemberDialogBody({
  open,
  onOpenChange,
  organizationId,
  membershipId,
  memberName,
  role,
  onDone,
  onStale,
}: ReactivateMemberDialogProps) {
  const { t } = useTranslation();
  const reactivate = useReactivateMember();
  const { submit, error, handleOpenChange } = useAccessSubmit({
    run: () => reactivate.mutateAsync({ organizationId, membershipId }),
    isPending: reactivate.isPending,
    onSuccess: (result) => {
      // The server's role: the row may have been stale about it.
      onDone({ changed: result.changed, role: result.role });
      onOpenChange(false);
    },
    onStale,
    onOpenChange,
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="reactivate-dialog"
        className="max-w-[calc(100vw-2rem)] sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("organizationAccess.reactivate.title")}</DialogTitle>
          <DialogDescription>
            <SafeText>{memberName}</SafeText>
            {` · ${t(`organizationAccess.roles.${role}`)}`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4">
          <p data-testid="reactivate-body" className="text-sm text-muted-foreground">
            {t(`organizationAccess.reactivate.body.${role}`)}
          </p>
          {error && <AccessErrorAlert testId="reactivate-error" message={error} />}
        </div>

        <DialogFooter>
          <Button
            data-testid="reactivate-cancel"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={reactivate.isPending}
          >
            {t("organizationAccess.cancel")}
          </Button>
          <Button
            data-testid="reactivate-submit"
            onClick={() => void submit()}
            disabled={reactivate.isPending}
          >
            {reactivate.isPending
              ? t("organizationAccess.reactivate.pending")
              : t("organizationAccess.reactivate.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
